import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { VLAN_COLORS } from '../lib/deviceIcons';
import { DEFAULT_CONN } from '../lib/connections';
import { findEdgeGlobal, findDeviceGlobal } from '../lib/detail';
import { edgeVlanMismatch, applyPortVlanConfig } from '../lib/vlanMismatch';
import { listProjects as lsProjects, saveProject, loadProjectDoc, newProjectId, projectExists, deleteProject as removeProject } from '../lib/projectStore';
import { safeImageHref, safeColor, sanitizeDocImages } from '../lib/safeUri';

const uid = () =>
  (typeof crypto !== 'undefined' && crypto.randomUUID
    ? crypto.randomUUID()
    : 'id-' + Math.random().toString(36).slice(2, 10));

const today = () => new Date().toISOString().slice(0, 10);

const TB_TEMPLATE_KEY = 'netdiagram-titleblock-template';

function loadTitleBlockTemplate() {
  try {
    const s = localStorage.getItem(TB_TEMPLATE_KEY);
    return s ? JSON.parse(s) : null;
  } catch {
    return null;
  }
}

function defaultTitleBlock() {
  return {
    projekt: 'Campus Netzwerk',
    titel: 'Netzwerkübersicht',
    zeichnungsnummer: 'NET-001',
    datum: today(),
    ersteller: '',
    geprueft: '',
    revision: 'A',
    firma: '',
    logo: '',
  };
}

export function makeInitialDoc(titleBlockOverride) {
  const pageId = uid();
  const tpl = titleBlockOverride || loadTitleBlockTemplate();
  return {
    name: 'Netzwerk-Projekt',
    zonesEnabled: false,
    zones: [],
    vlans: [
      { id: uid(), vlanId: 10, name: 'Management', network: '192.168.10.0/24', dhcpStart: '192.168.10.100', dhcpEnd: '192.168.10.200', color: VLAN_COLORS[0], zoneId: null },
      { id: uid(), vlanId: 20, name: 'Clients', network: '192.168.20.0/24', dhcpStart: '192.168.20.50', dhcpEnd: '192.168.20.250', color: VLAN_COLORS[1], zoneId: null },
    ],
    pages: [{ id: pageId, name: 'Seite 1', type: 'standard', nodes: [], edges: [], labelPos: 'below' }],
    titleBlock: tpl ? { ...defaultTitleBlock(), ...tpl } : defaultTitleBlock(),
  };
}

const clone = (o) => JSON.parse(JSON.stringify(o));

// Pages are always presented in a fixed order: standard sheets first, then
// detail pages, then location plans ("Positionspläne"). Within each group the
// original creation order is preserved (stable sort). This keeps the page tabs,
// sheet numbering and exports consistent regardless of when a page was added.
const PAGE_RANK = { standard: 0, detail: 1, location: 2 };
export function sortPages(pages) {
  return (pages || [])
    .map((p, i) => [p, i])
    .sort((a, b) => (PAGE_RANK[a[0].type] ?? 0) - (PAGE_RANK[b[0].type] ?? 0) || a[1] - b[1])
    .map((x) => x[0]);
}

export const useStore = create(
  persist(
    (set, get) => {
      const initDoc = makeInitialDoc();
      const snapshot = () => {
        const { doc, past } = get();
        set({ past: [...past, clone(doc)].slice(-60), future: [] });
      };
      // Debounced autosave of the ACTIVE project into the local project registry
      // so the switcher list + "last modified" stay accurate and nothing is lost.
      let saveTimer = null;
      const scheduleProjectSave = () => {
        if (saveTimer) clearTimeout(saveTimer);
        saveTimer = setTimeout(() => {
          saveTimer = null;
          const { projectId, doc } = get();
          if (projectId) saveProject(projectId, doc);
        }, 1200);
      };
      const setDoc = (updater) => {
        set((state) => ({ doc: updater(state.doc) }));
        scheduleProjectSave();
      };
      const ensureActive = (doc, currentId) =>
        doc.pages.find((p) => p.id === currentId) ? currentId : doc.pages[0]?.id;

      return {
        doc: initDoc,
        projectId: null,
        activePageId: initDoc.pages[0].id,
        selectedNodeId: null,
        selectedEdgeId: null,
        activeConnType: DEFAULT_CONN,
        language: 'de',
        // Canvas: snap-to-grid (16px). Default ON, toggleable from the canvas toolbar.
        snapEnabled: true,
        // Detail-view display prefs: per-port VLAN badges + a port/group VLAN legend.
        showPortVlanBadges: true,
        showVlanLegend: false,
        // Standard pages: show/hide the per-connection port-name labels.
        showPortLabels: true,
        // Search / VLAN highlight (transient UI state).
        highlightVlanRef: null,
        designCheckOpen: false,
        // Phase 16: edge whose connection/port config dialog is open (transient).
        connDialogEdgeId: null,
        past: [],
        future: [],

        setLanguage: (lang) => set({ language: lang }),
        setSnapEnabled: (v) => set({ snapEnabled: !!v }),
        setShowPortVlanBadges: (v) => set({ showPortVlanBadges: !!v }),
        setShowVlanLegend: (v) => set({ showVlanLegend: !!v }),
        setShowPortLabels: (v) => set({ showPortLabels: !!v }),
        setHighlightVlanRef: (ref) => set({ highlightVlanRef: ref || null }),
        setDesignCheckOpen: (v) => set({ designCheckOpen: !!v }),
        selectNode: (id) => set({ selectedNodeId: id, selectedEdgeId: null }),
        selectEdge: (id) => set({ selectedEdgeId: id, selectedNodeId: null }),
        setActiveConnType: (key) => set({ activeConnType: key }),
        setActivePage: (id) => set({ activePageId: id, selectedNodeId: null, selectedEdgeId: null }),

        // Jump to a device wherever it is defined (standard page that owns it),
        // switch to that page and select it. Used by search + design checks.
        focusNode: (nodeId) => {
          if (!nodeId) return;
          const { doc } = get();
          const owner = (doc.pages || []).find((p) => (p.nodes || []).some((n) => n.id === nodeId));
          if (owner) set({ activePageId: owner.id, selectedNodeId: nodeId, selectedEdgeId: null });
          else set({ selectedNodeId: nodeId, selectedEdgeId: null });
        },

        // Jump to a SPECIFIC page and select the device there. Used by the search
        // page-picker when a device appears on multiple pages (its owning
        // standard page + one or more detail drawings).
        focusNodeOnPage: (nodeId, pageId) => {
          if (!nodeId || !pageId) return;
          set({ activePageId: pageId, selectedNodeId: nodeId, selectedEdgeId: null });
        },

        // Per standard-page device-label placement: 'below' (default) or 'left'.
        setPageLabelPos: (pageId, pos) =>
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => (p.id === pageId ? { ...p, labelPos: pos === 'left' ? 'left' : 'below' } : p)),
          })),

        setPageGraph: (pageId, nodes, edges) =>
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) =>
              p.id === pageId ? { ...p, nodes: nodes ?? p.nodes, edges: edges ?? p.edges } : p
            ),
          })),

        addDevice: (deviceType, position, label) => {
          snapshot();
          const { activePageId } = get();
          const id = uid();
          const node = { id, type: 'device', position, data: { deviceType, label: label || '', vlanRef: null, ip: '', ports: [], panel: { groups: [] } } };
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => (p.id === activePageId ? { ...p, nodes: [...p.nodes, node] } : p)),
          }));
          set({ selectedNodeId: id });
          return id;
        },

        // Place an existing device (its MASTER) as an ALIAS/clone on the active
        // standard page. The alias only stores { aliasOf } + its own position;
        // all config is read from the master (single source of truth). No-op if
        // the device is already present on this page (as master or alias).
        addDeviceAlias: (masterId, position) => {
          if (!masterId) return null;
          snapshot();
          const id = uid();
          let createdId = null;
          setDoc((doc) => {
            const activeId = get().activePageId;
            const page = doc.pages.find((p) => p.id === activeId);
            if (!page || page.type !== 'standard') return doc;
            // Resolve to the canonical master id (handle being given an alias id).
            let realMaster = masterId;
            for (const p of doc.pages) {
              const n = (p.nodes || []).find((x) => x.id === masterId);
              if (n) {
                realMaster = n.data?.aliasOf || masterId;
                break;
              }
            }
            // Already present on this page? -> no-op.
            const present = (page.nodes || []).some((n) => n.id === realMaster || n.data?.aliasOf === realMaster);
            if (present) return doc;
            const i = (page.nodes || []).length;
            const pos = position || { x: 96 + (i % 4) * 150, y: 96 + Math.floor(i / 4) * 130 };
            const aliasNode = { id, type: 'device', position: pos, data: { aliasOf: realMaster } };
            createdId = id;
            return {
              ...doc,
              pages: doc.pages.map((p) => (p.id === activeId ? { ...p, nodes: [...(p.nodes || []), aliasNode] } : p)),
            };
          });
          if (createdId) set({ selectedNodeId: createdId, selectedEdgeId: null });
          return createdId;
        },

        updateNodeData: (nodeId, patch) => {
          const { activePageId } = get();
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) =>
              p.id === activePageId
                ? { ...p, nodes: p.nodes.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...patch } } : n)) }
                : p
            ),
          }));
        },

        // Update a device wherever it lives (used when editing from a detail page).
        updateDeviceGlobal: (nodeId, patch) => {
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) =>
              p.nodes
                ? { ...p, nodes: p.nodes.map((n) => (n.id === nodeId ? { ...n, data: { ...n.data, ...patch } } : n)) }
                : p
            ),
          }));
        },

        deleteNode: (nodeId) => {
          snapshot();
          const { activePageId, doc } = get();
          // Determine whether this is an alias placement or a master device.
          let targetNode = null;
          for (const p of doc.pages || []) {
            const n = (p.nodes || []).find((x) => x.id === nodeId);
            if (n) {
              targetNode = n;
              break;
            }
          }
          const isAlias = !!targetNode?.data?.aliasOf;

          if (isAlias) {
            // Remove ONLY this placement (active page) + edges on that page that
            // touch it. The master device and all other placements stay intact.
            setDoc((d) => ({
              ...d,
              pages: d.pages.map((p) =>
                p.id === activePageId
                  ? {
                      ...p,
                      nodes: (p.nodes || []).filter((n) => n.id !== nodeId),
                      edges: (p.edges || []).filter((e) => e.source !== nodeId && e.target !== nodeId),
                      lags: (p.lags || [])
                        .map((l) => ({ ...l, memberEdgeIds: (l.memberEdgeIds || []).filter((id) => (p.edges || []).some((e) => e.id === id && e.source !== nodeId && e.target !== nodeId)) }))
                        .filter((l) => (l.memberEdgeIds || []).length > 0),
                    }
                  : p
              ),
            }));
          } else {
            // Master device: remove it EVERYWHERE — the master node, every alias
            // placement of it (any page) and every edge touching any removed id.
            const removed = new Set([nodeId]);
            (doc.pages || []).forEach((p) =>
              (p.nodes || []).forEach((n) => {
                if (n.data?.aliasOf === nodeId) removed.add(n.id);
              })
            );
            setDoc((d) => ({
              ...d,
              pages: d.pages.map((p) => {
                const edges = (p.edges || []).filter((e) => !removed.has(e.source) && !removed.has(e.target));
                const remainingIds = new Set(edges.map((e) => e.id));
                return {
                  ...p,
                  nodes: (p.nodes || []).filter((n) => !removed.has(n.id)),
                  edges,
                  lags: (p.lags || [])
                    .map((l) => ({ ...l, memberEdgeIds: (l.memberEdgeIds || []).filter((id) => remainingIds.has(id)) }))
                    .filter((l) => (l.memberEdgeIds || []).length > 0),
                };
              }),
            }));
          }
          set((s) => ({ selectedNodeId: s.selectedNodeId === nodeId ? null : s.selectedNodeId }));
        },

        addEdgeConn: (edge) => {
          snapshot();
          const { activePageId, activeConnType } = get();
          const e = { id: uid(), connType: activeConnType, sourcePortId: null, targetPortId: null, ...edge };
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => (p.id === activePageId ? { ...p, edges: [...p.edges, e] } : p)),
          }));
          // Phase 16: if BOTH connected devices have configurable ports, open the
          // connection dialog so the user can assign ports (+ optionally resolve a
          // VLAN mismatch) right after drawing the link on a standard page.
          const doc2 = get().doc;
          const srcDev = findDeviceGlobal(doc2, e.source);
          const tgtDev = findDeviceGlobal(doc2, e.target);
          const srcPorts = (srcDev?.data?.ports || []).length;
          const tgtPorts = (tgtDev?.data?.ports || []).length;
          if (srcPorts > 0 && tgtPorts > 0) {
            set({ connDialogEdgeId: e.id, selectedEdgeId: e.id, selectedNodeId: null });
          }
        },

        // Phase 16 — connection/port config dialog controls.
        openConnDialog: (edgeId) => set({ connDialogEdgeId: edgeId, selectedEdgeId: edgeId, selectedNodeId: null }),
        closeConnDialog: () => set({ connDialogEdgeId: null }),
        // Copy the resolved VLAN config of one endpoint onto the other endpoint's
        // port (group-aware). fromKey is 'a' (source) or 'b' (target).
        copyEdgeVlanConfig: (edgeId, fromKey) => {
          const { doc, language } = get();
          const hit = findEdgeGlobal(doc, edgeId);
          if (!hit) return;
          const m = edgeVlanMismatch(doc, hit.edge, language);
          if (!m) return;
          const from = fromKey === 'b' ? m.b : m.a;
          const to = fromKey === 'b' ? m.a : m.b;
          snapshot();
          setDoc((d) => applyPortVlanConfig(d, to.deviceId, to.portId, { untaggedVlanRef: from.untaggedRef, taggedVlanRefs: from.taggedRefs }));
        },

        // Create an edge between two devices regardless of the active page (used
        // when drawing connections from a DETAIL page). The edge is stored on the
        // standard page that owns the source device so it round-trips correctly.
        addEdgeForDevices: ({ sourceDeviceId, targetDeviceId, sourcePortId, targetPortId, sourceSide, targetSide }) => {
          if (!sourceDeviceId || !targetDeviceId || sourceDeviceId === targetDeviceId) return;
          snapshot();
          const { activeConnType } = get();
          const edgeId = uid();
          let created = false;
          setDoc((doc) => {
            const ownerOf = (devId) => doc.pages.find((p) => p.type !== 'detail' && (p.nodes || []).some((n) => n.id === devId));
            const target = ownerOf(sourceDeviceId) || ownerOf(targetDeviceId) || doc.pages.find((p) => p.type !== 'detail');
            if (!target) return doc;
            const e = {
              id: edgeId,
              source: sourceDeviceId,
              target: targetDeviceId,
              connType: activeConnType || 'ethernet',
              sourcePortId: sourcePortId || null,
              targetPortId: targetPortId || null,
              sourceSide: sourceSide || undefined,
              targetSide: targetSide || undefined,
            };
            created = true;
            return { ...doc, pages: doc.pages.map((p) => (p.id === target.id ? { ...p, edges: [...(p.edges || []), e] } : p)) };
          });
          // A connection drawn on a DETAIL page already carries both ports (they
          // were chosen by dragging port-to-port). Only open the connection
          // dialog when there is an ACTUAL VLAN mismatch to resolve — identical
          // configs, both sides empty, or a passive/patch-panel port without an
          // active far peer produce no mismatch and therefore need no dialog.
          if (created) {
            set({ selectedEdgeId: edgeId, selectedNodeId: null });
            const { doc: freshDoc, language } = get();
            const hit = findEdgeGlobal(freshDoc, edgeId);
            const mismatch = hit ? edgeVlanMismatch(freshDoc, hit.edge, language) : null;
            if (mismatch) set({ connDialogEdgeId: edgeId });
          }
        },

        // Edge edits work GLOBALLY (search every page for the edge id) so they
        // also apply when the active page is a DETAIL plan — the edge itself
        // lives on the standard page that owns the devices. On standard pages
        // the edge is simply found on the active page, so behaviour is unchanged.
        updateEdge: (edgeId, patch) => {
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) =>
              (p.edges || []).some((e) => e.id === edgeId)
                ? { ...p, edges: p.edges.map((e) => (e.id === edgeId ? { ...e, ...patch } : e)) }
                : p
            ),
          }));
        },
        deleteEdge: (edgeId) => {
          snapshot();
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) =>
              (p.edges || []).some((e) => e.id === edgeId)
                ? {
                    ...p,
                    edges: p.edges.filter((e) => e.id !== edgeId),
                    lags: (p.lags || [])
                      .map((l) => ({ ...l, memberEdgeIds: (l.memberEdgeIds || []).filter((id) => id !== edgeId) }))
                      .filter((l) => (l.memberEdgeIds || []).length > 0),
                  }
                : p
            ),
          }));
          set((s) => ({
            selectedEdgeId: s.selectedEdgeId === edgeId ? null : s.selectedEdgeId,
            connDialogEdgeId: s.connDialogEdgeId === edgeId ? null : s.connDialogEdgeId,
          }));
        },

        // ---- LAG (Link Aggregation Group): bundles parallel edges between the
        // same two devices, similar to port groups. Stored on the page that owns
        // the member edges; each member edge also carries lagId for quick lookup
        // + draw.io round-trip. All ops resolve the owning page by edge/lag
        // membership (NOT the active page) so they also work from detail plans.
        createLag: (edgeIds, name) => {
          snapshot();
          const ids = Array.from(new Set(edgeIds || [])).filter(Boolean);
          if (!ids.length) return;
          const lagId = uid();
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => {
              if (!(p.edges || []).some((e) => ids.includes(e.id))) return p;
              const lag = { id: lagId, name: name || 'LAG', color: '#0B5FFF', memberEdgeIds: ids };
              return {
                ...p,
                lags: [...(p.lags || []), lag],
                edges: p.edges.map((e) => (ids.includes(e.id) ? { ...e, lagId } : e)),
              };
            }),
          }));
        },
        updateLag: (lagId, patch) => {
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) =>
              (p.lags || []).some((l) => l.id === lagId)
                ? { ...p, lags: (p.lags || []).map((l) => (l.id === lagId ? { ...l, ...patch } : l)) }
                : p
            ),
          }));
        },
        addEdgesToLag: (lagId, edgeIds) => {
          snapshot();
          const ids = Array.from(new Set(edgeIds || [])).filter(Boolean);
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => {
              if (!(p.lags || []).some((l) => l.id === lagId)) return p;
              return {
                ...p,
                lags: (p.lags || []).map((l) =>
                  l.id === lagId ? { ...l, memberEdgeIds: Array.from(new Set([...(l.memberEdgeIds || []), ...ids])) } : l
                ),
                edges: p.edges.map((e) => (ids.includes(e.id) ? { ...e, lagId } : e)),
              };
            }),
          }));
        },
        removeEdgeFromLag: (edgeId) => {
          snapshot();
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => {
              if (!(p.edges || []).some((e) => e.id === edgeId)) return p;
              return {
                ...p,
                edges: p.edges.map((e) => (e.id === edgeId ? { ...e, lagId: null } : e)),
                lags: (p.lags || [])
                  .map((l) => ({ ...l, memberEdgeIds: (l.memberEdgeIds || []).filter((id) => id !== edgeId) }))
                  .filter((l) => (l.memberEdgeIds || []).length > 0),
              };
            }),
          }));
        },
        deleteLag: (lagId) => {
          snapshot();
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) =>
              (p.lags || []).some((l) => l.id === lagId)
                ? {
                    ...p,
                    edges: p.edges.map((e) => (e.lagId === lagId ? { ...e, lagId: null } : e)),
                    lags: (p.lags || []).filter((l) => l.id !== lagId),
                  }
                : p
            ),
          }));
        },

        // ---- Device ports (defined per device, assigned to connections) -------
        addPorts: (nodeId, prefix, count) => {
          snapshot();
          const pfx = (prefix == null ? '' : String(prefix)).trim();
          let n = parseInt(count, 10);
          if (!Number.isFinite(n) || n < 1) n = 1;
          if (n > 96) n = 96;
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => ({
              ...p,
              nodes: (p.nodes || []).map((node) => {
                if (node.id !== nodeId) return node;
                const ports = node.data?.ports || [];
                // find current max trailing number for this prefix
                const esc = pfx.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
                const re = new RegExp('^' + esc + '(\\d+)$', 'i');
                let max = 0;
                ports.forEach((pt) => {
                  const m = re.exec(pt.name || '');
                  if (m) max = Math.max(max, parseInt(m[1], 10));
                });
                const added = [];
                for (let i = 1; i <= n; i++) {
                  const num = max + i;
                  added.push({ id: uid(), name: pfx ? `${pfx}${num}` : String(num), untaggedVlanRef: null, taggedVlanRefs: [] });
                }
                return { ...node, data: { ...node.data, ports: [...ports, ...added] } };
              }),
            })),
          }));
        },
        // Update a port. Accepts either a string (legacy: sets name) or a patch
        // object ({ name?, untaggedVlanRef?, taggedVlanRefs? }).
        updatePort: (nodeId, portId, patch) => {
          const p = typeof patch === 'string' ? { name: patch } : patch || {};
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((pg) => ({
              ...pg,
              nodes: (pg.nodes || []).map((node) =>
                node.id === nodeId
                  ? { ...node, data: { ...node.data, ports: (node.data?.ports || []).map((pt) => (pt.id === portId ? { ...pt, ...p } : pt)) } }
                  : node
              ),
            })),
          }));
        },
        deletePort: (nodeId, portId) => {
          snapshot();
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => ({
              ...p,
              nodes: (p.nodes || []).map((node) => {
                if (node.id !== nodeId) return node;
                const data = { ...node.data, ports: (node.data?.ports || []).filter((pt) => pt.id !== portId) };
                if (Array.isArray(data.portGroups)) {
                  data.portGroups = data.portGroups
                    .map((g) => ({ ...g, memberIds: (g.memberIds || []).filter((id) => id !== portId) }))
                    .filter((g) => (g.memberIds || []).length > 0);
                }
                return { ...node, data };
              }),
              edges: (p.edges || []).map((e) => ({
                ...e,
                sourcePortId: e.sourcePortId === portId ? null : e.sourcePortId,
                targetPortId: e.targetPortId === portId ? null : e.targetPortId,
              })),
            })),
          }));
        },

        // ---- Port config groups (tagged/untagged set once per group) ---------
        // A port belongs to at most one group; grouped ports inherit the group's
        // VLAN config and cannot be edited individually.
        createPortGroup: (nodeId, memberIds, name) => {
          snapshot();
          const ids = Array.from(new Set((memberIds || []).filter(Boolean)));
          if (!ids.length) return;
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => ({
              ...p,
              nodes: (p.nodes || []).map((node) => {
                if (node.id !== nodeId) return node;
                const ports = node.data?.ports || [];
                const existing = (node.data?.portGroups || []).map((g) => ({ ...g, memberIds: (g.memberIds || []).filter((id) => !ids.includes(id)) }));
                // inherit config from the first selected port that has one
                const seed = ports.find((pt) => ids.includes(pt.id) && (pt.untaggedVlanRef || (pt.taggedVlanRefs || []).length));
                const group = {
                  id: uid(),
                  name: name || '',
                  untaggedVlanRef: seed?.untaggedVlanRef || null,
                  taggedVlanRefs: Array.isArray(seed?.taggedVlanRefs) ? [...seed.taggedVlanRefs] : [],
                  memberIds: ids,
                };
                const groups = [...existing.filter((g) => (g.memberIds || []).length > 0), group];
                return { ...node, data: { ...node.data, portGroups: groups } };
              }),
            })),
          }));
        },
        updatePortGroup: (nodeId, groupId, patch) => {
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => ({
              ...p,
              nodes: (p.nodes || []).map((node) =>
                node.id === nodeId
                  ? { ...node, data: { ...node.data, portGroups: (node.data?.portGroups || []).map((g) => (g.id === groupId ? { ...g, ...patch } : g)) } }
                  : node
              ),
            })),
          }));
        },
        deletePortGroup: (nodeId, groupId) => {
          snapshot();
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => ({
              ...p,
              nodes: (p.nodes || []).map((node) =>
                node.id === nodeId
                  ? { ...node, data: { ...node.data, portGroups: (node.data?.portGroups || []).filter((g) => g.id !== groupId) } }
                  : node
              ),
            })),
          }));
        },
        addPortsToGroup: (nodeId, groupId, portIds) => {
          snapshot();
          const ids = Array.from(new Set((portIds || []).filter(Boolean)));
          if (!ids.length) return;
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => ({
              ...p,
              nodes: (p.nodes || []).map((node) => {
                if (node.id !== nodeId) return node;
                const groups = (node.data?.portGroups || [])
                  .map((g) =>
                    g.id === groupId
                      ? { ...g, memberIds: Array.from(new Set([...(g.memberIds || []), ...ids])) }
                      : { ...g, memberIds: (g.memberIds || []).filter((id) => !ids.includes(id)) }
                  )
                  .filter((g) => (g.memberIds || []).length > 0);
                return { ...node, data: { ...node.data, portGroups: groups } };
              }),
            })),
          }));
        },
        removePortFromGroup: (nodeId, portId) => {
          snapshot();
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => ({
              ...p,
              nodes: (p.nodes || []).map((node) => {
                if (node.id !== nodeId) return node;
                const groups = (node.data?.portGroups || [])
                  .map((g) => ({ ...g, memberIds: (g.memberIds || []).filter((id) => id !== portId) }))
                  .filter((g) => (g.memberIds || []).length > 0);
                return { ...node, data: { ...node.data, portGroups: groups } };
              }),
            })),
          }));
        },

        // ---- Virtual interfaces (SVI / VLAN-IF / sub-interface) --------------
        // A device may carry virtual interfaces independent of its physical
        // ports. Each interface has its own untagged/tagged VLAN config + IP, so
        // firewalls, L3 switches and virtualization hosts can document multiple
        // IPs. Global ops (search every page) so they work from detail plans too.
        addInterface: (nodeId, name) => {
          snapshot();
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => ({
              ...p,
              nodes: (p.nodes || []).map((node) => {
                if (node.id !== nodeId) return node;
                const ifaces = Array.isArray(node.data?.interfaces) ? node.data.interfaces : [];
                const iface = { id: uid(), name: name || `vlan${(ifaces.length + 1) * 10}`, untaggedVlanRef: null, taggedVlanRefs: [], ip: '' };
                return { ...node, data: { ...node.data, interfaces: [...ifaces, iface] } };
              }),
            })),
          }));
        },
        updateInterface: (nodeId, ifaceId, patch) => {
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => ({
              ...p,
              nodes: (p.nodes || []).map((node) =>
                node.id === nodeId
                  ? { ...node, data: { ...node.data, interfaces: (node.data?.interfaces || []).map((it) => (it.id === ifaceId ? { ...it, ...patch } : it)) } }
                  : node
              ),
            })),
          }));
        },
        deleteInterface: (nodeId, ifaceId) => {
          snapshot();
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => ({
              ...p,
              nodes: (p.nodes || []).map((node) =>
                node.id === nodeId
                  ? { ...node, data: { ...node.data, interfaces: (node.data?.interfaces || []).filter((it) => it.id !== ifaceId) } }
                  : node
              ),
            })),
          }));
        },

        addVlan: () => {
          snapshot();
          setDoc((doc) => {
            const used = doc.vlans.map((v) => v.vlanId);
            let nv = 10;
            while (used.includes(nv)) nv += 10;
            const color = VLAN_COLORS[doc.vlans.length % VLAN_COLORS.length];
            return { ...doc, vlans: [...doc.vlans, { id: uid(), vlanId: nv, name: 'VLAN ' + nv, network: '', dhcpStart: '', dhcpEnd: '', color, zoneId: null }] };
          });
        },
        updateVlan: (id, patch) => {
          const p = patch && typeof patch === 'object' ? { ...patch } : {};
          if ('color' in p) p.color = safeColor(p.color);
          setDoc((doc) => ({ ...doc, vlans: doc.vlans.map((v) => (v.id === id ? { ...v, ...p } : v)) }));
        },
        setZonesEnabled: (enabled) => {
          snapshot();
          setDoc((doc) => ({ ...doc, zonesEnabled: !!enabled }));
        },
        addZone: (name) => {
          snapshot();
          setDoc((doc) => {
            const zones = doc.zones || [];
            const n = (name || '').trim() || `Zone ${zones.length + 1}`;
            return { ...doc, zones: [...zones, { id: uid(), name: n }] };
          });
        },
        updateZone: (id, patch) => {
          setDoc((doc) => ({ ...doc, zones: (doc.zones || []).map((z) => (z.id === id ? { ...z, ...patch } : z)) }));
        },
        deleteZone: (id) => {
          snapshot();
          setDoc((doc) => ({
            ...doc,
            zones: (doc.zones || []).filter((z) => z.id !== id),
            vlans: doc.vlans.map((v) => (v.zoneId === id ? { ...v, zoneId: null } : v)),
          }));
        },
        deleteVlan: (id) => {
          snapshot();
          setDoc((doc) => ({
            ...doc,
            vlans: doc.vlans.filter((v) => v.id !== id),
            pages: doc.pages.map((p) => ({
              ...p,
              nodes: (p.nodes || []).map((n) => {
                const data = { ...n.data };
                if (data.vlanRef === id) data.vlanRef = null;
                if (Array.isArray(data.ports)) {
                  data.ports = data.ports.map((pt) => ({
                    ...pt,
                    untaggedVlanRef: pt.untaggedVlanRef === id ? null : (pt.untaggedVlanRef ?? null),
                    taggedVlanRefs: Array.isArray(pt.taggedVlanRefs) ? pt.taggedVlanRefs.filter((r) => r !== id) : [],
                  }));
                }
                if (Array.isArray(data.portGroups)) {
                  data.portGroups = data.portGroups.map((g) => ({
                    ...g,
                    untaggedVlanRef: g.untaggedVlanRef === id ? null : (g.untaggedVlanRef ?? null),
                    taggedVlanRefs: Array.isArray(g.taggedVlanRefs) ? g.taggedVlanRefs.filter((r) => r !== id) : [],
                  }));
                }
                if (Array.isArray(data.interfaces)) {
                  data.interfaces = data.interfaces.map((it) => {
                    const taggedIps = { ...(it.taggedIps || {}) };
                    if (taggedIps[id]) delete taggedIps[id];
                    return {
                      ...it,
                      untaggedVlanRef: it.untaggedVlanRef === id ? null : (it.untaggedVlanRef ?? null),
                      taggedVlanRefs: Array.isArray(it.taggedVlanRefs) ? it.taggedVlanRefs.filter((r) => r !== id) : [],
                      taggedIps,
                    };
                  });
                }
                return { ...n, data };
              }),
            })),
          }));
        },

        addPage: () => {
          snapshot();
          const id = uid();
          setDoc((doc) => ({ ...doc, pages: sortPages([...doc.pages, { id, name: 'Seite ' + (doc.pages.length + 1), type: 'standard', nodes: [], edges: [], labelPos: 'below' }]) }));
          set({ activePageId: id, selectedNodeId: null });
        },
        addDetailPage: () => {
          snapshot();
          const id = uid();
          setDoc((doc) => {
            const count = doc.pages.filter((p) => p.type === 'detail').length + 1;
            return { ...doc, pages: sortPages([...doc.pages, { id, name: 'Detail ' + count, type: 'detail', members: [], showStubs: false }]) };
          });
          set({ activePageId: id, selectedNodeId: null, selectedEdgeId: null });
        },
        addDetailMember: (pageId, deviceId) => {
          snapshot();
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => {
              if (p.id !== pageId || p.type !== 'detail') return p;
              if ((p.members || []).some((m) => m.deviceId === deviceId)) return p;
              const i = (p.members || []).length;
              const position = { x: 80 + (i % 3) * 300, y: 80 + Math.floor(i / 3) * 260 };
              return { ...p, members: [...(p.members || []), { deviceId, position }] };
            }),
          }));
          set({ selectedNodeId: deviceId, selectedEdgeId: null });
        },
        removeDetailMember: (pageId, deviceId) => {
          snapshot();
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) =>
              p.id === pageId && p.type === 'detail'
                ? { ...p, members: (p.members || []).filter((m) => m.deviceId !== deviceId) }
                : p
            ),
          }));
          set((s) => ({ selectedNodeId: s.selectedNodeId === deviceId ? null : s.selectedNodeId }));
        },
        updateDetailMemberPos: (pageId, deviceId, position) => {
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) =>
              p.id === pageId && p.type === 'detail'
                ? { ...p, members: (p.members || []).map((m) => (m.deviceId === deviceId ? { ...m, position } : m)) }
                : p
            ),
          }));
        },
        setDetailShowStubs: (pageId, value) => {
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => (p.id === pageId && p.type === 'detail' ? { ...p, showStubs: !!value } : p)),
          }));
        },
        setDetailViewMode: (pageId, mode) => {
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => (p.id === pageId && p.type === 'detail' ? { ...p, viewMode: mode === 'panel' ? 'panel' : 'list' } : p)),
          }));
        },

        // ---- Location plan ("Positionsplan") --------------------------------
        // A location page overlays device markers (small icons) + their
        // connections onto a physical background: an uploaded image, an uploaded
        // PDF (rasterised to an image) or an OpenStreetMap snapshot. The
        // background is stored as a single data URI so it persists offline and
        // exports identically.
        addLocationPage: () => {
          snapshot();
          const id = uid();
          setDoc((doc) => {
            const count = doc.pages.filter((p) => p.type === 'location').length + 1;
            return {
              ...doc,
              pages: sortPages([
                ...doc.pages,
                { id, name: 'Positionsplan ' + count, type: 'location', markers: [], background: { kind: 'none', dataUri: '' }, showConnections: true },
              ]),
            };
          });
          set({ activePageId: id, selectedNodeId: null, selectedEdgeId: null });
        },
        addLocationMarker: (pageId, deviceId) => {
          snapshot();
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => {
              if (p.id !== pageId || p.type !== 'location') return p;
              if ((p.markers || []).some((m) => m.deviceId === deviceId)) return p;
              const i = (p.markers || []).length;
              const position = { x: 120 + (i % 5) * 150, y: 120 + Math.floor(i / 5) * 130 };
              return { ...p, markers: [...(p.markers || []), { deviceId, position }] };
            }),
          }));
          set({ selectedNodeId: deviceId, selectedEdgeId: null });
        },
        removeLocationMarker: (pageId, deviceId) => {
          snapshot();
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) =>
              p.id === pageId && p.type === 'location'
                ? { ...p, markers: (p.markers || []).filter((m) => m.deviceId !== deviceId) }
                : p
            ),
          }));
          set((s) => ({ selectedNodeId: s.selectedNodeId === deviceId ? null : s.selectedNodeId }));
        },
        updateLocationMarkerPos: (pageId, deviceId, position) => {
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) =>
              p.id === pageId && p.type === 'location'
                ? { ...p, markers: (p.markers || []).map((m) => (m.deviceId === deviceId ? { ...m, position } : m)) }
                : p
            ),
          }));
        },
        setLocationBackground: (pageId, background) => {
          snapshot();
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) =>
              p.id === pageId && p.type === 'location'
                ? { ...p, background: { kind: background?.kind || 'none', dataUri: safeImageHref(background?.dataUri), meta: background?.meta || null } }
                : p
            ),
          }));
        },
        setLocationShowConnections: (pageId, value) => {
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => (p.id === pageId && p.type === 'location' ? { ...p, showConnections: !!value } : p)),
          }));
        },
        // Per-page element zoom (scales devices/edges only; CAD frame, legend and
        // title block stay fixed). Clamped to 25%..200%.
        setPageScale: (pageId, scale) => {
          const s = Math.max(0.25, Math.min(2, Number(scale) || 1));
          setDoc((doc) => ({
            ...doc,
            pages: doc.pages.map((p) => (p.id === pageId ? { ...p, contentScale: s } : p)),
          }));
        },
        renamePage: (id, name) => {
          snapshot();
          setDoc((doc) => ({ ...doc, pages: doc.pages.map((p) => (p.id === id ? { ...p, name } : p)) }));
        },
        deletePage: (id) => {
          const { doc } = get();
          if (doc.pages.length <= 1) return;
          snapshot();
          setDoc((d) => ({ ...d, pages: d.pages.filter((p) => p.id !== id) }));
          const pages = get().doc.pages;
          set((s) => ({ activePageId: s.activePageId === id ? pages[0]?.id : s.activePageId, selectedNodeId: null }));
        },

        setTitleBlock: (patch) => {
          const p = patch && typeof patch === 'object' ? { ...patch } : {};
          if ('logo' in p) p.logo = safeImageHref(p.logo);
          setDoc((doc) => ({ ...doc, titleBlock: { ...doc.titleBlock, ...p } }));
        },
        saveTitleBlockTemplate: () => {
          try {
            localStorage.setItem(TB_TEMPLATE_KEY, JSON.stringify(get().doc.titleBlock));
          } catch {}
        },
        importTitleBlock: (tb) => {
          snapshot();
          const src = tb && typeof tb === 'object' ? tb : {};
          setDoc((doc) => ({ ...doc, titleBlock: { ...defaultTitleBlock(), ...src, logo: safeImageHref(src.logo) } }));
        },
        setProjectName: (name) => setDoc((doc) => ({ ...doc, name })),

        loadDoc: (doc) => {
          // Importing a file (or opening a shared link) creates a NEW local
          // project so the currently open one is never overwritten.
          get().saveCurrentProject();
          const safe = sanitizeDocImages(doc);
          const sorted = { ...safe, pages: sortPages(safe.pages) };
          const id = newProjectId();
          saveProject(id, sorted);
          set({ doc: sorted, projectId: id, activePageId: sorted.pages?.[0]?.id, selectedNodeId: null, selectedEdgeId: null, connDialogEdgeId: null, past: [], future: [] });
        },
        newProject: (titleBlockOverride) => {
          // Save the current project, then start a fresh one as its own entry.
          // An optional title-block override lets "Neu" adopt the current
          // project's title block (Schriftkopf).
          get().saveCurrentProject();
          const d = makeInitialDoc(titleBlockOverride || null);
          const id = newProjectId();
          saveProject(id, d);
          set({ doc: d, projectId: id, activePageId: d.pages[0].id, selectedNodeId: null, selectedEdgeId: null, connDialogEdgeId: null, past: [], future: [] });
        },

        // ---- Local multi-project management ---------------------------------
        // Called once after hydration: registers the active project if needed
        // (assigning + persisting a stable id) and mirrors the current doc.
        // Idempotent — safe to call multiple times (e.g. React StrictMode).
        ensureProject: () => {
          let { projectId, doc } = get();
          if (!projectId || !projectExists(projectId)) {
            projectId = projectId || newProjectId();
            set({ projectId });
          }
          saveProject(projectId, doc);
        },
        // Immediately persist the active project into the registry.
        saveCurrentProject: () => {
          const { projectId, doc } = get();
          if (projectId) saveProject(projectId, doc);
        },
        // List of local projects ([{id,name,updatedAt}]), newest first. The
        // caller (dialog) syncs the active project first so it appears current.
        listProjects: () => {
          get().saveCurrentProject();
          return lsProjects();
        },
        // Switch to another local project (saves the current one first).
        switchProject: (id) => {
          const { projectId } = get();
          if (id === projectId) return;
          get().saveCurrentProject();
          const d = loadProjectDoc(id);
          if (!d) return;
          const sorted = { ...d, pages: sortPages(d.pages) };
          set({ doc: sorted, projectId: id, activePageId: sorted.pages?.[0]?.id, selectedNodeId: null, selectedEdgeId: null, connDialogEdgeId: null, past: [], future: [] });
        },
        // Remove a local project. If the ACTIVE project is deleted, fall back to
        // the most recently updated remaining project (or a fresh blank one).
        deleteProject: (id) => {
          const { projectId } = get();
          removeProject(id);
          if (id !== projectId) return;
          const remaining = lsProjects();
          if (remaining.length) {
            const d = loadProjectDoc(remaining[0].id);
            if (d) {
              const sorted = { ...d, pages: sortPages(d.pages) };
              set({ doc: sorted, projectId: remaining[0].id, activePageId: sorted.pages?.[0]?.id, selectedNodeId: null, selectedEdgeId: null, connDialogEdgeId: null, past: [], future: [] });
              return;
            }
          }
          const nd = makeInitialDoc();
          const nid = newProjectId();
          saveProject(nid, nd);
          set({ doc: nd, projectId: nid, activePageId: nd.pages[0].id, selectedNodeId: null, selectedEdgeId: null, connDialogEdgeId: null, past: [], future: [] });
        },

        undo: () => {
          const { past, doc, future } = get();
          if (!past.length) return;
          const prev = past[past.length - 1];
          set((s) => ({ doc: prev, past: past.slice(0, -1), future: [doc, ...future].slice(0, 60), activePageId: ensureActive(prev, s.activePageId) }));
          scheduleProjectSave();
        },
        redo: () => {
          const { future, doc, past } = get();
          if (!future.length) return;
          const next = future[0];
          set((s) => ({ doc: next, future: future.slice(1), past: [...past, doc].slice(-60), activePageId: ensureActive(next, s.activePageId) }));
          scheduleProjectSave();
        },
      };
    },
    {
      name: 'netdiagram-store-v1',
      storage: createJSONStorage(() => localStorage),
      partialize: (s) => ({ doc: s.doc, projectId: s.projectId, language: s.language, activePageId: s.activePageId, activeConnType: s.activeConnType, snapEnabled: s.snapEnabled, showPortVlanBadges: s.showPortVlanBadges, showVlanLegend: s.showVlanLegend, showPortLabels: s.showPortLabels }),
      onRehydrateStorage: () => (state) => {
        // Migrate the removed "logical diagram" auto page to the new cable list.
        if (state && state.activePageId === 'auto-logical') state.activePageId = 'auto-cablelist';
        // Normalise page order for projects created before the fixed ordering
        // (standard → detail → location) was introduced.
        if (state && state.doc && Array.isArray(state.doc.pages)) {
          state.doc.pages = sortPages(state.doc.pages);
        }
        // Defense in depth: persisted documents are sanitized on hydration too
        // (strips non-image logo/background URIs and malformed colors).
        if (state && state.doc) {
          state.doc = sanitizeDocImages(state.doc);
        }
        // NOTE: project-registry seeding happens in `ensureProject()` (called
        // once after hydration from the Editor) so `projectId` is assigned via
        // the store's set() and reliably persisted — avoiding duplicate entries.
      },
    }
  )
);

export function getActivePage(state) {
  return state.doc.pages.find((p) => p.id === state.activePageId) || null;
}
